import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ComponentUpdate } from "../../store/slices/component";
import { AddHomeSection } from "./AddSection";
import { DeleteHomeAppScreenList, GetHomeAppScreenList, updateSectionSorting } from "../../common/CommonApi";
import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import Swal from "sweetalert2";
import { ArrowLeft, GripVertical, LayoutPanelTop, Plus, Trash2 } from "lucide-react";

const SortableItem = ({ sec, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: sec._id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  };

  return (
    <div ref={setNodeRef} style={style}>
      {children({ attributes, listeners })}
    </div>
  );
};

const ViewApp = () => {
  const [searchParams] = useSearchParams();
  const id = searchParams.get("catid");
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [sections, setSections] = useState([]);
  const [isHomeScreen, setisHomeScreen] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchSections = useCallback(async () => {
    try {
      setLoading(true);
      const resp = await GetHomeAppScreenList(id);
      setSections(resp.data.data || []);
    } catch (error) {
      console.error("Error fetching home sections:", error);
      setSections([]);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchSections();
  }, [fetchSections]);

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = sections.findIndex((section) => section._id === active.id);
    const newIndex = sections.findIndex((section) => section._id === over.id);
    const newItems = arrayMove(sections, oldIndex, newIndex);
    const updatedItems = newItems.map((item, index) => ({
      ...item,
      sort_order: index + 1
    }));

    setSections(updatedItems);

    try {
      const payload = updatedItems.map((item) => ({
        id: item._id,
        sort_order: item.sort_order
      }));
      await updateSectionSorting(payload);
    } catch (err) {
      console.error("Sorting failed", err);
    }
  };

  const openModal = () => {
    setisHomeScreen(true);
    dispatch(ComponentUpdate({ categoryId: id }));
  };

  const closeModel = (val) => setisHomeScreen(val);

  const handleDelete = async (sectionId) => {
    const confirm = await Swal.fire({
      title: "Delete this section?",
      text: "This section will be removed from the home app configuration.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#30D5C8",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, delete it"
    });

    if (confirm.isConfirmed) {
      await DeleteHomeAppScreenList(sectionId);
      fetchSections();
      Swal.fire("Deleted", "Section has been deleted.");
    }
  };

  return (
    <Wrapper>
      <PageHeader>
        <HeaderLeft>
          <BackButton type="button" onClick={() => navigate("/home")}>
            <ArrowLeft size={16} />
            Back
          </BackButton>
          <div>
            <Title>Added Sections</Title>
            <Subtitle>Arrange and manage sections for this category. Drag cards to update display order.</Subtitle>
          </div>
        </HeaderLeft>
        <AddBtn type="button" onClick={openModal}>
          <Plus size={16} />
          Add Section
        </AddBtn>
      </PageHeader>

      <StatsGrid>
        <StatCard>
          <StatNumber>{sections.length}</StatNumber>
          <StatLabel>Total Sections</StatLabel>
        </StatCard>
        <StatCard>
          <StatNumber>{sections.filter((section) => section.status).length}</StatNumber>
          <StatLabel>Active Sections</StatLabel>
        </StatCard>
      </StatsGrid>

      {loading && (
        <Loader>
          <img src="/assets/loading.gif" alt="Loading sections" />
        </Loader>
      )}

      {!loading && sections.length === 0 ? (
        <EmptyState>
          <LayoutPanelTop size={34} />
          <strong>No sections found</strong>
          <span>Add a section to start building this home category.</span>
        </EmptyState>
      ) : null}

      {!loading && sections.length > 0 && (
        <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={sections.map((section) => section._id)} strategy={verticalListSortingStrategy}>
            <SectionGrid>
              {sections.map((sec) => (
                <SortableItem key={sec._id} sec={sec}>
                  {({ attributes, listeners }) => (
                    <SectionCard>
                      <CardHeader>
                        <DragHandle type="button" {...attributes} {...listeners} aria-label="Drag section">
                          <GripVertical size={18} />
                        </DragHandle>
                        <TitleBlock>
                          <SectionName>{sec.title}</SectionName>
                          <SectionDesc>{sec.description || "No description added"}</SectionDesc>
                        </TitleBlock>
                        <StatusBadge active={sec.status}>{sec.status ? "Active" : "Inactive"}</StatusBadge>
                      </CardHeader>

                      <MetaGrid>
                        <MetaTag>{sec.type}</MetaTag>
                        <MetaTag>{sec.layout_type}</MetaTag>
                        <MetaTag>{sec.screen}</MetaTag>
                        <MetaTag>{sec.image_type}</MetaTag>
                      </MetaGrid>

                      <CardFooter>
                        <FooterMeta>
                          <strong>{sec.component?.length || 0}</strong>
                          <span>Components</span>
                        </FooterMeta>
                        <FooterMeta>
                          <strong>{sec.sort_order || "-"}</strong>
                          <span>Sort Order</span>
                        </FooterMeta>
                        <DeleteButton type="button" onClick={() => handleDelete(sec._id)}>
                          <Trash2 size={15} />
                          Delete
                        </DeleteButton>
                      </CardFooter>
                    </SectionCard>
                  )}
                </SortableItem>
              ))}
            </SectionGrid>
          </SortableContext>
        </DndContext>
      )}

      {isHomeScreen && (
        <AddHomeSection
          categoryId={id}
          onSaved={fetchSections}
          closeModel={closeModel}
        />
      )}
    </Wrapper>
  );
};

export default ViewApp;

const Wrapper = styled.div`
  min-height: 100vh;
  padding: 28px;
  background: #f8fafc;

  @media (max-width: 768px) {
    padding: 0;
    min-height: auto;
  }
`;

const PageHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 18px;

  @media (max-width: 760px) {
    flex-direction: column;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;

  @media (max-width: 760px) {
    flex-direction: column;
  }
`;

const BackButton = styled.button`
  min-height: 40px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0 14px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  background: #fff;
  color: #334155;
  font-size: 14px;
  font-weight: 900;
  cursor: pointer;

  &:hover {
    background: #f1f5f9;
  }
`;

const Title = styled.h2`
  margin: 0;
  color: #0f172a;
  font-size: 28px;
  font-weight: 900;
`;

const Subtitle = styled.p`
  margin: 6px 0 0;
  color: #64748b;
  font-size: 14px;
`;

const AddBtn = styled.button`
  min-height: 42px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0 16px;
  border: 0;
  border-radius: 8px;
  background: #30d5c8;
  color: #fff;
  font-size: 14px;
  font-weight: 900;
  cursor: pointer;

  &:hover {
    background: #26a79d;
  }

  @media (max-width: 480px) {
    justify-content: center;
    width: 100%;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
  margin-bottom: 18px;
`;

const StatCard = styled.div`
  padding: 18px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);
`;

const StatNumber = styled.div`
  color: #30d5c8;
  font-size: 28px;
  font-weight: 900;
`;

const StatLabel = styled.div`
  margin-top: 4px;
  color: #64748b;
  font-size: 13px;
  font-weight: 800;
`;

const SectionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 18px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const SectionCard = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);
  overflow: hidden;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
`;

const DragHandle = styled.button`
  width: 34px;
  height: 34px;
  flex: 0 0 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  background: #f8fafc;
  color: #64748b;
  cursor: grab;

  &:active {
    cursor: grabbing;
  }
`;

const TitleBlock = styled.div`
  min-width: 0;
  flex: 1;
`;

const SectionName = styled.h3`
  margin: 0;
  color: #0f172a;
  font-size: 16px;
  font-weight: 900;
`;

const SectionDesc = styled.p`
  display: -webkit-box;
  margin: 6px 0 0;
  color: #64748b;
  font-size: 13px;
  line-height: 1.45;
  overflow: hidden;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const StatusBadge = styled.span`
  flex: 0 0 auto;
  padding: 5px 9px;
  border-radius: 999px;
  background: ${({ active }) => (active ? "#d4f5e9" : "#fde4e4")};
  color: ${({ active }) => (active ? "#056d3b" : "#a61b1b")};
  font-size: 11px;
  font-weight: 900;
`;

const MetaGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 0 16px 16px;
`;

const MetaTag = styled.span`
  padding: 5px 9px;
  border-radius: 999px;
  background: #ecfeff;
  color: #0f766e;
  font-size: 12px;
  font-weight: 800;
`;

const CardFooter = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr)) auto;
  gap: 10px;
  align-items: center;
  padding: 14px 16px;
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;

  @media (max-width: 480px) {
    grid-template-columns: 1fr 1fr;

    > :last-child {
      grid-column: 1 / -1;
      justify-content: center;
    }
  }
`;

const FooterMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;

  strong {
    color: #0f172a;
    font-size: 16px;
  }

  span {
    color: #64748b;
    font-size: 11px;
    font-weight: 800;
    text-transform: uppercase;
  }
`;

const DeleteButton = styled.button`
  min-height: 36px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0 12px;
  border: 1px solid #fecaca;
  border-radius: 8px;
  background: #fff;
  color: #dc2626;
  font-size: 12px;
  font-weight: 900;
  cursor: pointer;

  &:hover {
    background: #fef2f2;
  }
`;

const Loader = styled.div`
  min-height: 260px;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 78px;
    height: 78px;
    object-fit: contain;
  }
`;

const EmptyState = styled.div`
  min-height: 260px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 8px;
  color: #64748b;

  strong {
    color: #0f172a;
    font-size: 16px;
  }
`;
